import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ScalyProvider } from './contexts/ScalyContext';
import { Toaster } from './components/ui/sonner';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import PracticePage from './pages/PracticePage';
import AIGeneratorPage from './pages/AIGeneratorPage';
import FavoritesPage from './pages/FavoritesPage';
import ProgressPage from './pages/ProgressPage';
import SubscriptionPage from './pages/SubscriptionPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelPage from './pages/PaymentCancelPage';
import AdminPage from './pages/AdminPage';
import QuestionManagerPage from './pages/QuestionManagerPage';
import MonsterBattlePage from './pages/MonsterBattlePage';
import FlyerPage from './pages/FlyerPage';
import WorksheetsPage from './pages/WorksheetsPage';
import WorksheetDetailPage from './pages/WorksheetDetailPage';
import WorksheetPrintPage from './pages/WorksheetPrintPage';
import WeightedAssessmentsHubPage from './pages/WeightedAssessmentsHubPage';
import WeightedAssessmentRunnerPage from './pages/WeightedAssessmentRunnerPage';
import WeightedAssessmentResultsPage from './pages/WeightedAssessmentResultsPage';
import OralPracticePage from './pages/OralPracticePage';
import AdminOralPassagesPage from './pages/AdminOralPassagesPage';
import TutorRegisterPage from './pages/TutorRegisterPage';
import TutorDashboardPage from './pages/TutorDashboardPage';
import MarketplaceBrowsePage, { MarketplaceSuccessPage, MarketplaceCancelPage } from './pages/MarketplacePage';
import MarketplaceGradePage from './pages/MarketplaceGradePage';
import MarketplacePrintPage from './pages/MarketplacePrintPage';
import AdminMarketplacePage from './pages/AdminMarketplacePage';
import DiagnosticPage from './pages/DiagnosticPage';
import DiagnosticTakePage from './pages/DiagnosticTakePage';
import DiagnosticResultsPage from './pages/DiagnosticResultsPage';
import WorksheetMarkerPage from './pages/WorksheetMarkerPage';
import QABotPage from './pages/QABotPage';
import VideoLessonsPage from './pages/VideoLessonsPage';
import AdminVideoGenerator from './pages/AdminVideoGenerator';
import ProfScalyChatBubble from './components/ProfScalyChatBubble';
import './App.css';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_e77e6e6f-0eb9-4cf5-990d-5085ee85d2a5/artifacts/o9we2o4z_file_00000000b8947207b6be688fc35a0ba2.png"
            alt="Prof. Scaly"
            className="w-24 h-24 mx-auto mb-4 object-contain animate-pulse"
          />
          <p className="text-lg text-slate-600 font-semibold">Loading...</p>
        </div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" replace />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_e77e6e6f-0eb9-4cf5-990d-5085ee85d2a5/artifacts/o9we2o4z_file_00000000b8947207b6be688fc35a0ba2.png"
            alt="Prof. Scaly"
            className="w-24 h-24 mx-auto mb-4 object-contain animate-pulse"
          />
          <p className="text-lg text-slate-600 font-semibold">Loading...</p>
        </div>
      </div>
    );
  }
  
  return user ? <Navigate to="/dashboard" replace /> : children;
};

function App() {
  return (
    <AuthProvider>
      <ScalyProvider>
        <BrowserRouter>
          <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPasswordPage />
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password"
            element={<ResetPasswordPage />}
          />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <DashboardPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/practice/:moduleId"
            element={
              <PrivateRoute>
                <PracticePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/ai-generate"
            element={
              <PrivateRoute>
                <AIGeneratorPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/favorites"
            element={
              <PrivateRoute>
                <FavoritesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/progress"
            element={
              <PrivateRoute>
                <ProgressPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/subscription"
            element={
              <PrivateRoute>
                <SubscriptionPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/payment/success"
            element={
              <PrivateRoute>
                <PaymentSuccessPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/payment/cancel"
            element={
              <PrivateRoute>
                <PaymentCancelPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <PrivateRoute>
                <AdminPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/questions"
            element={
              <PrivateRoute>
                <QuestionManagerPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/monsters"
            element={
              <PrivateRoute>
                <MonsterBattlePage />
              </PrivateRoute>
            }
          />
          <Route path="/flyer" element={<FlyerPage />} />
          <Route
            path="/worksheets"
            element={
              <PrivateRoute>
                <WorksheetsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/worksheets/:id"
            element={
              <PrivateRoute>
                <WorksheetDetailPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/worksheets/:id/print"
            element={
              <PrivateRoute>
                <WorksheetPrintPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/weighted-assessments"
            element={
              <PrivateRoute>
                <WeightedAssessmentsHubPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/weighted-assessments/:id"
            element={
              <PrivateRoute>
                <WeightedAssessmentRunnerPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/weighted-assessments/:id/results"
            element={
              <PrivateRoute>
                <WeightedAssessmentResultsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/oral-practice"
            element={
              <PrivateRoute>
                <OralPracticePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/oral-passages"
            element={
              <PrivateRoute>
                <AdminOralPassagesPage />
              </PrivateRoute>
            }
          />
          {/* Tutors' Treasure Box (Marketplace) */}
          <Route path="/tutor/register" element={<TutorRegisterPage />} />
          <Route
            path="/tutor/dashboard"
            element={
              <PrivateRoute>
                <TutorDashboardPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/tutor/worksheet-marker"
            element={
              <PrivateRoute>
                <WorksheetMarkerPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/qa-bot"
            element={
              <PrivateRoute>
                <QABotPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/marketplace"
            element={
              <PrivateRoute>
                <MarketplaceBrowsePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/marketplace/success"
            element={
              <PrivateRoute>
                <MarketplaceSuccessPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/marketplace/cancel"
            element={
              <PrivateRoute>
                <MarketplaceCancelPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/marketplace/grade/:purchaseId"
            element={
              <PrivateRoute>
                <MarketplaceGradePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/marketplace/print/:purchaseId"
            element={
              <PrivateRoute>
                <MarketplacePrintPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/marketplace"
            element={
              <PrivateRoute>
                <AdminMarketplacePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/diagnostic"
            element={
              <PrivateRoute>
                <DiagnosticPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/diagnostic/take/:sessionId"
            element={
              <PrivateRoute>
                <DiagnosticTakePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/diagnostic/results/:sessionId"
            element={
              <PrivateRoute>
                <DiagnosticResultsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/video-lessons"
            element={
              <PrivateRoute>
                <VideoLessonsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/video-generator"
            element={
              <PrivateRoute>
                <AdminVideoGenerator />
              </PrivateRoute>
            }
          />
        </Routes>
        <ProfScalyChatBubble />
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </ScalyProvider>
    </AuthProvider>
  );
}

export default App;